import React, { useEffect, useRef, useState } from 'react';
import { v4 as uuid } from 'uuid';

import { useEntryFinder } from '~Components/EntryFinder';
import { useDispatch, useSelector, usePaymentMethods } from '~Hooks';
import * as actions from '~actions';

import { SERVICE_TYPE } from '~../__queries__/globalTypes';
import { GetGiftCardItem_item } from '~Queries/__queries__';
import { Box } from '~Styles';
import PaymentMethodSelector from './PaymentMethodSelector';
import PaymentStatus from '~Components/PaymentStatus';
import { PaymentOptions } from '.';

type TransactionState =
  | { status: 'AWAITING_PAYMENT_METHOD' }
  | { status: 'AWAITING_PAYMENT' }
  | { status: 'SUCCESS' }
  | { status: 'ERROR'; error: string };

interface PaymentHandlerProps {
  onSuccess: () => void;
  onError: (error: Error, payload?: any) => void;
  item: GetGiftCardItem_item;
  amount: number;
  options?: PaymentOptions;
}

const PaymentHandler = ({ onSuccess, onError, amount, item, options }: PaymentHandlerProps) => {
  const dispatch = useDispatch();
  const paymentMethods = usePaymentMethods();
  const findEntryByItemId = useEntryFinder();

  // If we need to select a payment method, set the state to AWAITING_PAYMENT_METHOD else AWAITING_PAYMENT
  const [transactionState, setTransactionState] = useState<TransactionState>({
    status: paymentMethods.all.length <= 1 ? 'AWAITING_PAYMENT' : 'AWAITING_PAYMENT_METHOD',
  });
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<string | undefined>(
    paymentMethods.all.length <= 1 ? paymentMethods.getDefaultMethod() : undefined,
  );

  const orderId = useRef(uuid());
  const payment = useSelector((state) =>
    state.POSPayments.find((p) => p.order === orderId.current),
  );

  const createPayment = () => {
    if (!selectedPaymentMethod) {
      return;
    }

    const entry = findEntryByItemId(item.id);
    if (!entry) {
      onError(new Error('Item entry could not be found'));
      return;
    }

    dispatch(
      actions.initiateOrder({
        id: orderId.current,
        method: selectedPaymentMethod,
        serviceType: SERVICE_TYPE.EAT_HERE,
        orderNumber: 'GC',
      }),
    );

    const category = item.categories[0];

    dispatch(
      actions.addItemToOrder(orderId.current, {
        count: 1,
        description: '',
        entryId: entry.id,
        removedVariations: [],
        amount: amount,
        categoryId: category.nameLang.se || category.nameLang.en,
        discount: 0,
        fraction: 1,
        itemId: item.id,
        title: item.titleLang.se,
        variations: [],
        vatRate: item.vatRate,
      }),
    );

    dispatch(
      actions.initiatePayment(orderId.current, {
        canCancel: false,
        method: selectedPaymentMethod,
        disableTip: options?.disableTip,
        meta: {
          transactionId: uuid(),
        },
        amount: amount || 0,
      }),
    );
  };

  useEffect(() => {
    if (transactionState.status === 'AWAITING_PAYMENT') {
      if (!payment) {
        createPayment();
      }
      return;
    }
  }, [transactionState]);

  // Handle payment result
  useEffect(() => {
    if (!payment || payment.status === 'PENDING') {
      return;
    }

    if (payment.status === 'COMPLETED') {
      onSuccess();
    }

    if (payment.status === 'FAILED') {
      // Remove POS order and payment
      dispatch({
        type: 'REMOVE_POS_PAYMENT',
        id: payment.id,
      });

      dispatch({
        type: 'REMOVE_POS_ORDER',
        id: orderId,
      });

      onError(new Error('Payment failed.'));
    }
  }, [payment?.status]);

  return (
    <div>
      {transactionState.status === 'AWAITING_PAYMENT_METHOD' && (
        <Box fluid>
          <PaymentMethodSelector
            methods={options?.methods?.length ? options.methods : paymentMethods.all}
            onSelect={(method) => {
              setSelectedPaymentMethod(method.id);
              setTransactionState({ status: 'AWAITING_PAYMENT' });
            }}
          />
        </Box>
      )}
      {/* Step 1 - Show payment status */}
      {transactionState.status !== 'AWAITING_PAYMENT_METHOD' && (
        <div>{payment && <PaymentStatus paymentId={payment.id} status={payment.status} />}</div>
      )}
    </div>
  );
};

export default PaymentHandler;
